/**
 * Desbloqueo con Face ID.
 *
 * Usa un passkey del propio iPhone (WebAuthn con autenticador de
 * plataforma). No sustituye a la contrasena del servidor: Suwayomi solo
 * entiende autenticacion basica y no hay nada al otro lado que pueda
 * comprobar una firma. Lo que hace es guardar la puerta de la app, y por eso
 * mismo funciona sin conexion: la cara la valida el iPhone en local.
 *
 * Se guarda solo el identificador del passkey. La llave privada no sale del
 * Secure Enclave, asi que aqui no hay nada que proteger mas alla de no
 * perder ese identificador.
 */

const CLAVE = 'yomou.faceid'
const ESPERA = 60000

function aBase64(datos: ArrayBuffer): string {
  let binario = ''
  for (const byte of new Uint8Array(datos)) binario += String.fromCharCode(byte)
  return btoa(binario)
}

// El parametro de Uint8Array no sobra: sin el, TypeScript lo ensancha a
// ArrayBufferLike (que admite SharedArrayBuffer) y WebAuthn pide BufferSource.
function deBase64(texto: string): Uint8Array<ArrayBuffer> {
  const binario = atob(texto)
  const bytes = new Uint8Array(binario.length)
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i)
  return bytes
}

/** Un reto nuevo cada vez. No lo comprueba nadie, pero repetirlo seria feo. */
function reto(): Uint8Array<ArrayBuffer> {
  return crypto.getRandomValues(new Uint8Array(32))
}

/** Si el dispositivo tiene Face ID (o Touch ID, o codigo) utilizable aqui. */
export async function hayFaceId(): Promise<boolean> {
  if (!window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable) return false
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
  } catch {
    return false
  }
}

export function estaActivado(): boolean {
  return localStorage.getItem(CLAVE) !== null
}

export function desactivar() {
  localStorage.removeItem(CLAVE)
}

/**
 * Registra el passkey. Tiene que salir de un gesto del usuario: Safari
 * rechaza la llamada si no viene de un toque suyo.
 */
export async function activar(usuario: string): Promise<void> {
  const credencial = (await navigator.credentials.create({
    publicKey: {
      challenge: reto(),
      // Sin `id` el navegador usa el dominio actual, que es lo que queremos
      rp: { name: 'Yomou' },
      user: {
        id: new TextEncoder().encode(usuario),
        name: usuario,
        displayName: usuario,
      },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 },
      ],
      authenticatorSelection: {
        // El iPhone, no una llave USB
        authenticatorAttachment: 'platform',
        userVerification: 'required',
        residentKey: 'preferred',
      },
      timeout: ESPERA,
    },
  })) as PublicKeyCredential | null

  if (!credencial) throw new Error('No se pudo crear el passkey')
  localStorage.setItem(CLAVE, aBase64(credencial.rawId))
}

/** Pide la cara. `false` es un rechazo; si algo va mal, lanza. */
export async function desbloquear(): Promise<boolean> {
  const guardado = localStorage.getItem(CLAVE)
  if (!guardado) return true

  const credencial = await navigator.credentials.get({
    publicKey: {
      challenge: reto(),
      allowCredentials: [{ type: 'public-key', id: deBase64(guardado) }],
      userVerification: 'required',
      timeout: ESPERA,
    },
  })
  return credencial !== null
}
